import { FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import { Input, Select } from 'components/Shared';
import type { SelectOption } from 'contracts/Common';
import {
  DeleteWarehouseSchedulingRestrictionActions as DeleteActions,
  CreateWarehouseSchedulingRestrictionActions as CreateActions,
} from 'store/ducks/warehouseSchedulingRestrictions';
import { useValidation } from 'hooks';
import {
  forwardRef,
  ForwardRefExoticComponent,
  RefAttributes,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { AppDispatch, RootState } from 'store';
import * as Yup from 'yup';
import * as S from './styles';
import { WarehouseSchedulingRestriction } from 'contracts/WarehouseSchedulingRestrictions';

export interface Ref {
  resetForm: () => void;
}

interface Props {
  onUpdate?: () => void;
  warehouseId: string | number;
  dockId: string | number;
}

interface ISchedulingRestrictionsManager extends ForwardRefExoticComponent<
  Props & RefAttributes<Ref>
> {}

interface ItemProps extends WarehouseSchedulingRestriction {}

const SchedulingRestrictionsManager: ISchedulingRestrictionsManager =
  forwardRef<Ref, Props>(({ warehouseId, dockId, onUpdate }, ref) => {
    const { data: dock } = useSelector((state: RootState) => state.fetchDock);

    const dispatch: AppDispatch = useDispatch();
    const formRef = useRef<FormHandles>(null);

    const { handleFormErrors, handleApiErrors } = useValidation();

    /**
     * this component will not fetch vehicleTypes here,
     * we lifted it to a upper level (DocksManager component)
     * to avoid dispatching multiple times
     * */
    const { loading: loadingCarriers, data: carriers } = useSelector(
      (state: RootState) => state.listCarriers
    );

    const { loading: creating, validationErrors } = useSelector(
      (state: RootState) => state.createWarehouseSchedulingRestriction
    );

    const { id: deletingId } = useSelector(
      (state: RootState) => state.deleteWarehouseSchedulingRestriction
    );

    /**
     * we will also filter the list of vehicleTypes to only show
     * the ones that are not already bound to the dock
     * */

    const carrierOptions: SelectOption[] | undefined = useMemo(() => {
      if (!carriers?.length) return undefined;

      const alreadyAdded =
        dock?.schedulingRestrictions?.map(
          (restriction) => restriction.carrierId
        ) || [];

      return carriers
        .filter((carrier) => !alreadyAdded.includes(carrier.id))
        .map((carrier) => ({
          value: carrier.id,
          label: carrier.tradeName,
        }));
    }, [carriers, dock]);

    const onDelete = useCallback(
      (id?: number) => dispatch(DeleteActions.request(id, onUpdate)),
      [dispatch, onUpdate]
    );

    const onSuccess = useCallback((): void => {
      onUpdate && onUpdate();
      formRef.current?.reset();
      formRef.current?.setData({
        loadingCount: 3,
        unloadingCount: 3,
      });
    }, [onUpdate]);

    const onSubmit = useCallback(
      async (data: any): Promise<void> => {
        try {
          formRef.current?.setErrors({});
          const schema = Yup.object().shape({
            carrierId: Yup.number()
              .typeError('Selecione a transportadora')
              .required('Selecione a transportadora'),
            loadingCount: Yup.number()
              .required('Obrigatório')
              .typeError('Obrigatório'),
            unloadingCount: Yup.number()
              .required('Obrigatório')
              .typeError('Obrigatório'),
          });

          if (
            Number(data.loadingCount) <= 0 &&
            Number(data.unloadingCount) <= 0
          ) {
            formRef.current?.setErrors({
              loadingCount: 'Informe ao menos um valor maior que zero',
              unloadingCount: 'Informe ao menos um valor maior que zero',
            });

            return;
          }

          const validData = await schema.validate(data, {
            abortEarly: false,
          });
          dispatch(
            CreateActions.request(
              { ...validData, warehouseId, dockId },
              onSuccess
            )
          );
        } catch (error) {
          handleFormErrors(error, formRef);
        }
      },
      [dispatch, dockId, handleFormErrors, onSuccess, warehouseId]
    );

    const Item = useCallback(
      ({
        carrier,
        loadingCount,
        unloadingCount,
        id,
      }: ItemProps): JSX.Element => {
        const { id: carrierId, tradeName } = carrier;

        return (
          <S.ListItem>
            <S.Column>{carrierId}</S.Column>
            <S.Column>{tradeName}</S.Column>
            <S.Column>{loadingCount}</S.Column>
            <S.Column>{unloadingCount}</S.Column>
            <S.ActionsColumn>
              <S.ActionButton
                disabled={deletingId === carrierId}
                onClick={() => onDelete(id)}
                mood="danger"
              >
                {deletingId === carrierId ? (
                  <S.ActivityIndicator />
                ) : (
                  <S.TrashIcon />
                )}
              </S.ActionButton>
            </S.ActionsColumn>
          </S.ListItem>
        );
      },
      [deletingId, onDelete]
    );

    useImperativeHandle(
      ref,
      () => ({
        resetForm: () => {
          formRef.current?.reset();
          formRef.current?.setErrors({});
        },
      }),
      []
    );

    useEffect(() => {
      handleApiErrors(validationErrors, formRef);
    }, [handleApiErrors, validationErrors]);

    return (
      <S.Container>
        <Form ref={formRef} onSubmit={onSubmit}>
          <S.FormRow>
            <Select
              name="carrierId"
              label="Transportadora"
              options={carrierOptions}
              isLoading={loadingCarriers}
              menuPortalTarget={document.body}
              menuPlacement="top"
            />
            <Input
              name="loadingCount"
              title="Máximo de vezes que a transportadora pode criar um agendamento do tipo CARREGAMENTO"
              label="Limite carregamento"
              type="number"
              min={0}
              defaultValue={3}
            />
            <Input
              name="unloadingCount"
              title="Máximo de vezes que a transportadora pode criar um agendamento do tipo DESCARREGAMENTO"
              label="Limite descarregamento"
              type="number"
              min={0}
              defaultValue={3}
            />
            <S.Button type="submit">
              {creating ? <S.ActivityIndicator /> : 'Adicionar'}
            </S.Button>
          </S.FormRow>
        </Form>
        {!dock?.schedulingRestrictions?.length ? (
          <S.EmptyListPlaceholder>
            Esse armazém ainda não possui restrições para quantidade de
            agendamentos permitidos por transportadora
          </S.EmptyListPlaceholder>
        ) : (
          <>
            <S.ListHeader>
              <div>ID</div>
              <div>TRANSPORTADORA</div>
              <div>LIMITE CARREGAMENTO</div>
              <div>LIMITE DESCARREGAMENTO</div>
            </S.ListHeader>
            {dock?.schedulingRestrictions?.map((props) => (
              <Item {...props} key={props.id} />
            ))}
          </>
        )}
      </S.Container>
    );
  });

export default SchedulingRestrictionsManager;
